import React, { useEffect, useState } from "react";
import { Modal, Button, ButtonGroup, Table, Badge } from "react-bootstrap";
import styles from "./cards/GenericFHIRViewerModal.module.css";
import { useDispatch, useStore, useSelector } from "react-redux";
import { FaPaperclip } from "react-icons/fa";
import { fetchProviderByReference } from "./services/providerService";


function flattenObject(obj, parentKey = "") {
  let flat = {};
  for (let key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const newKey = parentKey ? `${parentKey}.${key}` : key;
    const value = obj[key];

    if (value === null || value === undefined) {
      flat[newKey] = "";
    } else if (Array.isArray(value)) {
      if (value.every(v => typeof v === "object")) {
        value.forEach((item, index) => {
          const nested = flattenObject(item, `${newKey}[${index}]`);
          Object.assign(flat, nested);
        });
      } else {
        flat[newKey] = value.join(", ");
      }
    } else if (typeof value === "object") {
      Object.assign(flat, flattenObject(value, newKey));
    } else {
      flat[newKey] = String(value);
    }
  }
  return flat;
}

function getPractitionerName(ref, providers = {}) {
  const id = ref?.split("/")?.[1];
  const provider = id ? providers[id] : null;
  if (!provider) return ref || "Unknown";

  return (
    provider?.name?.[0]?.text ||
    [provider.name?.[0]?.given?.join(" "), provider.name?.[0]?.family]
      .filter(Boolean)
      .join(" ") ||
    id
  );
}

function formatPriority(priority) {
  if (!priority) return <Badge bg="secondary">Unknown</Badge>;

  const colorMap = {
    stat: "danger",
    asap: "warning",
    urgent: "info",
    routine: "success"
  };

  const level = priority.toLowerCase();
  const color = colorMap[level] || "secondary";

  return <Badge bg={color}>{priority.toUpperCase()}</Badge>;
}

const CommViewer = ({ show, onClose, resource, fhirClient, onReply, onReplyAll }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const providers = useSelector(state => state.providers);
  const [viewMode, setViewMode] = useState("message");
  const [errorModalShow, setErrorModalShow] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");

  const senderRef = resource?.sender?.reference;
  const recipientRefs = resource?.recipient?.map(r => r.reference) || [];

  // 🧠 Fetch missing providers
  useEffect(() => {
    const loadMissingProviders = async () => {
      if (!fhirClient) return;
      const refs = [senderRef, ...recipientRefs];
      const missing = refs.filter(ref => {
        const id = ref?.split("/")?.[1];
        return id && !providers[id];
      });
      for (const ref of missing) {
        await fetchProviderByReference(ref, fhirClient, store.getState, dispatch);
      }
    };

    if (show && resource) {
      loadMissingProviders();
    }
  }, [resource, show, fhirClient]);

  const flatData = resource ? flattenObject(resource) : null;
  const sender = getPractitionerName(senderRef, providers);
  const recipients =
    recipientRefs.map(ref => getPractitionerName(ref, providers)).join(", ") || "None";
  const priority = formatPriority(resource?.priority);
  const sent = resource?.sent ? new Date(resource.sent).toLocaleString() : "Unknown";

  const handleAttachmentDownload = async (att, index) => {
    try {
      if (att.data) {
        const byteCharacters = atob(att.data);
        const byteArray = new Uint8Array([...byteCharacters].map(c => c.charCodeAt(0)));
        const blob = new Blob([byteArray], { type: att.contentType || "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = att.title || `attachment-${index + 1}`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (att.url && fhirClient) {
        const binary = await fhirClient.request(att.url);
        const byteCharacters = atob(binary.data);
        const byteArray = new Uint8Array([...byteCharacters].map(c => c.charCodeAt(0)));
        const blob = new Blob([byteArray], { type: binary.contentType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = binary.title || att.title || `attachment-${index + 1}`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        setErrorModalMessage("No data or retrievable URL for attachment.");
        setErrorModalShow(true);
      }
    } catch (err) {
      console.error("❌ Failed to download attachment:", err);
      setErrorModalMessage("An error occurred while downloading the attachment.");
      setErrorModalShow(true);
    }
  };

  const payload =
    resource?.payload?.length > 0
      ? resource.payload.map((p, i) => {
        if (p.contentString) {
          return <p key={i}>{p.contentString}</p>;
        }

        const attachment = p.contentAttachment;
        if (attachment) {
          const title = attachment.title || `Attachment ${i + 1}`;
          return (
            <p key={i}>
              <FaPaperclip className="me-2" />
              <button
                className="btn btn-link p-0"
                onClick={() => handleAttachmentDownload(attachment, i)}
              >
                {title}
              </button>
            </p>
          );
        }

        return (
          <p key={i} style={{ fontStyle: "italic", color: "#999" }}>
            [Unrecognized payload format]
          </p>
        );
      })
      : "(No content)";

  const title =
    resource?.topic?.coding?.[0]?.display ||
    resource?.topic?.text ||
    "Communication Details";

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable centered>
      <Modal.Header className="bg-light d-flex justify-content-between align-items-center">
        <Modal.Title className="mb-0">{title}</Modal.Title>
        <ButtonGroup>
          <Button
            variant={viewMode === "message" ? "primary" : "outline-primary"}
            size="sm"
            onClick={() => setViewMode("message")}
          >
            Message
          </Button>
          <Button
            variant={viewMode === "table" ? "primary" : "outline-primary"}
            size="sm"
            onClick={() => setViewMode("table")}
          >
            HTML
          </Button>
          <Button
            variant={viewMode === "json" ? "primary" : "outline-primary"}
            size="sm"
            onClick={() => setViewMode("json")}
          >
            JSON
          </Button>
        </ButtonGroup>
      </Modal.Header>

      <Modal.Body className={styles.fhirViewerModal}>
        {viewMode === "json" && resource ? (
          <pre
            style={{
              fontSize: "0.85rem",
              background: "#f8f9fa",
              padding: "1rem",
              borderRadius: "0.3rem"
            }}
          >
            {JSON.stringify(resource, null, 2)}
          </pre>
        ) : viewMode === "message" ? (
          <div className="px-2" style={{ fontSize: "0.95rem" }}>
            <p><strong>From:</strong> Dr. {sender}</p>
            <p><strong>To:</strong> {recipients}</p>
            <p><strong>Priority:</strong> {priority}</p>
            <p><strong>Sent:</strong> {sent}</p>
            <hr />
            <div>{payload}</div>
          </div>
        ) : viewMode === "table" && flatData ? (
          <Table bordered responsive size="sm">
            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(flatData).map(([key, value]) => (
                <tr key={key}>
                  <td><code>{key}</code></td>
                  <td>{value}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p>No resource data available.</p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div>
            <Button
              variant="outline-success"
              onClick={() => onReply && onReply(resource)}
              className="me-2"
              disabled={!resource}
            >
              Reply
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => onReplyAll && onReplyAll(resource)}
              disabled={!resource}
            >
              Reply All
            </Button>
          </div>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CommViewer;
